import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight, FolderTree, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { StoreCategory } from '@/api/products'

// 树节点
interface CategoryNode {
	id: number
	pid: number
	cate_name: string
	children?: CategoryNode[]
}

interface CategoryTreeSelectProps {
	categories: StoreCategory[]
	selectedId: number
	onSelect: (id: number) => void
}

// 扁平列表转树
function buildTree(categories: StoreCategory[]): CategoryNode[] {
	const map = new Map<number, CategoryNode>()
	const roots: CategoryNode[] = []

	for (const c of categories) {
		map.set(c.id, { id: c.id, pid: c.pid, cate_name: c.cate_name })
	}

	for (const c of categories) {
		const node = map.get(c.id)!
		if (c.pid === 0 || !map.has(c.pid)) {
			roots.push(node)
		} else {
			const parent = map.get(c.pid)!
			if (!parent.children) parent.children = []
			parent.children.push(node)
		}
	}

	return roots
}

// 在树中查找节点名称
function findCategoryName(nodes: CategoryNode[], id: number): string | null {
	for (const node of nodes) {
		if (node.id === id) return node.cate_name
		if (node.children) {
			const found = findCategoryName(node.children, id)
			if (found) return found
		}
	}
	return null
}

// 递归树节点
function TreeNode({
	node,
	selectedId,
	onSelect,
	level = 0,
}: {
	node: CategoryNode
	selectedId: number
	onSelect: (id: number) => void
	level?: number
}) {
	const [isExpanded, setIsExpanded] = useState(false)
	const hasChildren = !!node.children?.length
	const isSelected = selectedId === node.id

	return (
		<div className="select-none">
			<div className="flex items-center gap-1" style={{ paddingLeft: `${level * 16 + 4}px` }}>
				{hasChildren ? (
					<button
						type="button"
						className="flex h-5 w-5 shrink-0 items-center justify-center"
						onClick={() => setIsExpanded((v) => !v)}
					>
						{isExpanded ? (
							<ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
						) : (
							<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
						)}
					</button>
				) : (
					<span className="w-5 shrink-0" aria-hidden="true" />
				)}
				<button
					type="button"
					className={cn(
						'flex min-w-0 flex-1 items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors',
						isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
					)}
					onClick={() => onSelect(node.id)}
				>
					<span className="truncate">{node.cate_name}</span>
				</button>
			</div>
			{hasChildren && isExpanded && (
				<div>
					{node.children!.map((child) => (
						<TreeNode
							key={child.id}
							node={child}
							selectedId={selectedId}
							onSelect={onSelect}
							level={level + 1}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export function CategoryTreeSelect({ categories, selectedId, onSelect }: CategoryTreeSelectProps) {
	const [isOpen, setIsOpen] = useState(false)

	const treeData = useMemo(() => buildTree(categories), [categories])
	const selectedName = selectedId ? findCategoryName(treeData, selectedId) : null

	const handleSelect = (id: number) => {
		onSelect(id)
		setIsOpen(false)
	}

	const handleClear = () => {
		onSelect(0)
		setIsOpen(false)
	}

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className={cn(
						'h-[38px] w-[160px] justify-start text-left font-normal',
						!selectedId && 'text-muted-foreground'
					)}
				>
					<FolderTree className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
					<span className="min-w-0 flex-1 truncate">
						{selectedName ?? '全部分类'}
					</span>
					<ChevronDown className="ml-1 h-4 w-4 shrink-0 text-muted-foreground" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[260px] p-0" align="start">
				{selectedId > 0 && (
					<div className="flex items-center justify-end border-b px-2 py-1">
						<Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={handleClear}>
							<X className="mr-1 h-3 w-3" />
							清除选择
						</Button>
					</div>
				)}
				<div className="max-h-[320px] overflow-auto p-2">
					{treeData.length === 0 ? (
						<div className="p-4 text-center text-sm text-muted-foreground">暂无分类数据</div>
					) : (
						treeData.map((node) => (
							<TreeNode
								key={node.id}
								node={node}
								selectedId={selectedId}
								onSelect={handleSelect}
							/>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
